import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { CashBalance } from "@/lib/types";
import { money, fmtDateTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, Scale, Save, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const CashBalanceCard: React.FC<{ reloadSignal?: number }> = ({ reloadSignal }) => {
  const [balance, setBalance] = useState<CashBalance | null>(null);
  const [real, setReal] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await client.get("/cash/balance");
    setBalance(res.data);
    if (res.data.saldo_real_reportado != null) setReal(String(res.data.saldo_real_reportado));
  };

  useEffect(() => {
    load();
  }, [reloadSignal]);

  const save = async () => {
    const val = parseFloat(real);
    if (isNaN(val)) {
      toast.error("Ingresa un saldo válido");
      return;
    }
    setSaving(true);
    try {
      await client.post("/cash/balance", { reported_balance: val, note: "" });
      toast.success("Saldo real actualizado");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!balance) return null;
  const dif = balance.diferencia;
  const conciliado = dif != null && Math.abs(dif) < 0.01;

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="cash-balance-card">
      <div className="border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Saldo esperado</span>
          <Wallet className="h-4 w-4 text-[#DD0000]" strokeWidth={1.5} />
        </div>
        <div className="mt-3 text-2xl font-medium tabular text-slate-900" data-testid="balance-expected">
          {money(balance.saldo_esperado)}
        </div>
        <div className="mt-1 text-xs text-slate-400">Saldo inicial + entradas reales − salidas</div>
        {(() => {
          const bd = balance as any;
          if (bd.saldo_inicial == null) return null;
          return (
            <div className="mt-3 space-y-1 border-t border-slate-100 pt-2 text-[11px] text-slate-500" data-testid="balance-breakdown">
              {bd.fecha_corte && (
                <div className="flex justify-between">
                  <span>Saldo inicial ({bd.fecha_corte})</span>
                  <span className="tabular">{money(bd.saldo_inicial)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>+ Entradas registradas</span>
                <span className="tabular text-emerald-600">{money(bd.entradas_manuales)}</span>
              </div>
              <div className="flex justify-between" data-testid="balance-deposits">
                <span>+ Depósitos de plataformas</span>
                <span className="tabular text-emerald-600">{money(bd.depositos_plataformas)}</span>
              </div>
              <div className="pl-3 text-[10px] text-slate-400">
                Shopify {money(bd.depositos_detalle?.shopify || 0)} · ML {money(bd.depositos_detalle?.mercadolibre || 0)} · Amazon {money(bd.depositos_detalle?.amazon || 0)}
              </div>
              <div className="flex justify-between">
                <span>− Salidas registradas</span>
                <span className="tabular text-red-600">−{money(bd.salidas_desde_corte)}</span>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Saldo real en caja / banco</span>
          <Scale className="h-4 w-4 text-slate-600" strokeWidth={1.5} />
        </div>
        <div className="mt-3 flex gap-2">
          <Input
            type="number"
            value={real}
            onChange={(e) => setReal(e.target.value)}
            placeholder="0.00"
            data-testid="balance-real-input"
            className="rounded-none border-slate-300 text-lg tabular"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid="balance-save"
            className="flex items-center gap-2 rounded-none bg-[#DD0000] px-3 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
          </button>
        </div>
        {balance.updated_at && (
          <div className="mt-1 text-xs text-slate-400">Actualizado {fmtDateTime(balance.updated_at)}</div>
        )}
      </div>

      <div className={`border p-5 ${conciliado ? "border-emerald-200 bg-emerald-50" : dif == null ? "border-slate-200 bg-white" : "border-red-200 bg-red-50"}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Diferencia</span>
          {conciliado ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
          ) : (
            <AlertTriangle className="h-4 w-4 text-red-500" strokeWidth={1.5} />
          )}
        </div>
        <div className={`mt-3 text-2xl font-medium tabular ${conciliado ? "text-emerald-700" : dif == null ? "text-slate-400" : "text-red-600"}`} data-testid="balance-diff">
          {dif == null ? "—" : money(dif)}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {dif == null
            ? "Captura el saldo real para conciliar"
            : conciliado
            ? "Caja conciliada ✓"
            : dif > 0
            ? "Hay más dinero del registrado: falta registrar entradas"
            : "Falta dinero: revisa salidas no registradas"}
        </div>
      </div>
    </div>
  );
};

export default CashBalanceCard;
